import React, { FC, ReactElement, ReactNode, useRef, useState } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { XYCoord } from 'dnd-core';
import {
  Card,
  Form,
  InputGroup,
  DropdownButton,
  Dropdown,
  ButtonGroup,
  Button,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import i18next from 'i18next';
import { getIn, useFormikContext } from 'formik';
import Switch from 'react-switch';
import LexicalEditor from '../../../../src/components/textEditor/LexicalEditor';
import MediaPlayer from '../../../components/MediaPlayer';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { ExerciseFormValue, TrainingPlanFormValues, isSimpleExerciseType } from '../trainingPlan.types';
import { ExerciseType } from '../../../../../shared/utils/enums';
import { useIsVideoFeedbackEnabled } from '../../../hooks/useTenantSettings';
import { parseNumberInput } from '../../../util/number-utils';

type ExerciseItemProps = {
  index: number;
  tabIndex: number;
};

const preventDrag = (e: React.DragEvent): void => e.preventDefault();
const preventScrollChange = (e: React.WheelEvent<HTMLInputElement>): void => e.currentTarget.blur();

export const ExerciseItem: FC<ExerciseItemProps> = ({ index, tabIndex }: ExerciseItemProps): ReactElement => {
  const isVideFeedbackEnabled: boolean = useIsVideoFeedbackEnabled();
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLSpanElement>(null);

  const { values, touched, errors, setFieldValue, setFieldTouched, handleBlur } = useFormikContext<TrainingPlanFormValues>();

  const userLanguage = i18next.language;
  const fieldBase = `trainings.${tabIndex}.exercises.${index}`;
  const exerciseValues: ExerciseFormValue = getIn(values, fieldBase);
  const exerciseTouched = getIn(touched, fieldBase);
  const exerciseErrors = getIn(errors, fieldBase);
  const exerciseType = exerciseValues.exerciseType;

  const isSimpleType = isSimpleExerciseType(exerciseType);
  const exerciseCount = values.trainings[tabIndex].exercises.length;

  const setExerciseField = async <K extends keyof ExerciseFormValue>(
    field: K,
    value: ExerciseFormValue[K],
    touch = true
  ): Promise<void> => {
    const path = `${fieldBase}.${String(field)}`;
    await setFieldValue(path, value);
    if (touch) {
      await setFieldTouched(path, true, false);
    }
  };

  const handleNumberChange = async (field: keyof ExerciseFormValue, rawValue: string): Promise<void> => {
    await setExerciseField(field, parseNumberInput(rawValue));
  };

  const handleUnitChange = async (unit: 'sec' | 'reps'): Promise<void> => {
    await setExerciseField('unit', unit);
    await setExerciseField(unit === 'reps' ? 'time' : 'repetition', undefined, false);
  };

  const moveCard = async (dragIndex: number, hoverIndex: number): Promise<void> => {
    const exercises = [...values.trainings[tabIndex].exercises];
    const [dragCard] = exercises.splice(dragIndex, 1);
    exercises.splice(hoverIndex, 0, dragCard);
    await setFieldValue(`trainings.${tabIndex}.exercises`, exercises);
  };

  const moveUp = (): void => {
    void moveCard(index, index - 1);
  };

  const moveDown = (): void => {
    void moveCard(index, index + 1);
  };

  const handleRemoveExercise = async (): Promise<void> => {
    const exercises = values.trainings[tabIndex].exercises.filter((_, i) => i !== index);
    await setFieldValue(`trainings.${tabIndex}.exercises`, exercises);
    void setFieldTouched(`trainings.${tabIndex}.exercises`, true);
    setShowConfirmModal(false);
  };

  const handleCustomDescriptionToggle = async (enabled: boolean): Promise<void> => {
    await setExerciseField('useCustomDescription', enabled);

    if (!enabled && exerciseValues.exercise) {
      const isEnglish = userLanguage === 'en';
      await setExerciseField('name', isEnglish ? exerciseValues.exercise.enName : exerciseValues.exercise.deName);
      await setExerciseField('description', isEnglish ? exerciseValues.exercise.enDescription : exerciseValues.exercise.deDescription);
    }
  };

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover(item: { index: number }, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }

      const hoverClientY = (clientOffset as XYCoord).y - hoverRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY - 10) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY + 10) {
        return;
      }

      void moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'card',
    item: () => ({ id: `${tabIndex}-${index}`, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drop(ref);
  preview(ref);
  drag(dragHandleRef);

  const renderSetsField = (): ReactNode => (
    <div>
      <Form.Group>
        <Form.Label>{i18next.t('exerciseCard.form.sets')}</Form.Label>
        <Form.Control
          type="number"
          min="1"
          name={`${fieldBase}.sets`}
          id={`sets-${index}-${tabIndex}`}
          placeholder={i18next.t('exerciseCard.form.sets')}
          value={exerciseValues.sets ?? ''}
          onChange={(e) => handleNumberChange('sets', e.target.value)}
          onBlur={handleBlur}
          onWheel={preventScrollChange}
          isInvalid={exerciseTouched?.sets && exerciseErrors?.sets}
        />
        <Form.Control.Feedback type="invalid">{exerciseErrors?.sets}</Form.Control.Feedback>
      </Form.Group>
    </div>
  );

  const renderTimeField = (unitLabel: string, feedbackKey: string): ReactNode => (
    <Form.Group>
      <Form.Label>{i18next.t('exerciseCard.form.amount')}</Form.Label>
      <InputGroup>
        <Form.Control
          type="number"
          min="1"
          name={`${fieldBase}.time`}
          id={`time-${index}-${tabIndex}`}
          placeholder={i18next.t('exerciseCard.form.time')}
          value={exerciseValues.time ?? ''}
          onChange={(e) => handleNumberChange('time', e.target.value)}
          onBlur={handleBlur}
          onWheel={preventScrollChange}
          isInvalid={exerciseTouched?.time && exerciseErrors?.time}
        />
        <Button variant="primary" size="sm" className="btn-dd-normal">
          {i18next.t(`exerciseCard.form.dropdown.${unitLabel}`)}
        </Button>
        <Form.Control.Feedback key={feedbackKey} type="invalid">
          {exerciseErrors?.time}
        </Form.Control.Feedback>
      </InputGroup>
    </Form.Group>
  );

  const renderUnitDropdown = (): ReactNode => (
    <DropdownButton
      variant="primary"
      as={ButtonGroup}
      size="sm"
      title={i18next.t(`exerciseCard.form.dropdown.${exerciseValues.unit}`)}
      className="p-0 dropdown-time"
    >
      <Dropdown.Item onClick={() => handleUnitChange('sec')}>
        {i18next.t('exerciseCard.form.dropdown.sec')}
      </Dropdown.Item>
      <Dropdown.Item onClick={() => handleUnitChange('reps')}>
        {i18next.t('exerciseCard.form.dropdown.reps')}
      </Dropdown.Item>
    </DropdownButton>
  );

  const renderDynamicAmountField = (): ReactNode => {
    const isReps = exerciseValues.unit === 'reps';
    const fieldName = isReps ? 'repetition' : 'time';
    const fieldValue = isReps ? exerciseValues.repetition : exerciseValues.time;
    const fieldTouched = isReps ? exerciseTouched?.repetition : exerciseTouched?.time;
    const fieldError = isReps ? exerciseErrors?.repetition : exerciseErrors?.time;

    return (
      <Form.Group>
        <Form.Label>{i18next.t('exerciseCard.form.amount')}</Form.Label>
        <InputGroup>
          <Form.Control
            type="number"
            min="1"
            name={`${fieldBase}.${fieldName}`}
            id={`${fieldName}-${index}-${tabIndex}`}
            placeholder={i18next.t(`exerciseCard.form.${fieldName}`)}
            value={fieldValue ?? ''}
            onChange={(e) => handleNumberChange(fieldName, e.target.value)}
            onBlur={handleBlur}
            onWheel={preventScrollChange}
            isInvalid={fieldTouched && fieldError}
          />
          {renderUnitDropdown()}
          <Form.Control.Feedback key={`dynamic-${fieldName}-${fieldValue}`} type="invalid">
            {fieldError}
          </Form.Control.Feedback>
        </InputGroup>
      </Form.Group>
    );
  };

  const renderExerciseTypeFields = (): ReactNode => {
    if (exerciseType === ExerciseType.ENDURANCE) {
      return renderTimeField('min', `endurance-time-${exerciseValues.time}`);
    }

    if (exerciseType === ExerciseType.STATIC) {
      return (
        <>
          {renderSetsField()}
          {renderTimeField('sec', `static-time-${exerciseValues.time}`)}
        </>
      );
    }

    if (exerciseType === ExerciseType.DYNAMIC) {
      return (
        <>
          {renderSetsField()}
          {renderDynamicAmountField()}
        </>
      );
    }

    return null;
  };

  const skipPausePopover = (
    <Popover id="popover-basic">
      <Card>
        <Card.Body className="font-15">
          <p>{i18next.t('exerciseCard.form.skipPauseDescription')}</p>
        </Card.Body>
      </Card>
    </Popover>
  );

  const renderDeleteButton = (): ReactNode => (
    <Button
      variant="link"
      className="text-danger p-0 flex-shrink-0"
      onClick={() => setShowConfirmModal(true)}
      aria-label={i18next.t('generic.delete')}
    >
      <i className="fas fa-times" aria-hidden="true" />
    </Button>
  );

  const renderReorderControls = (): ReactNode => (
    <div className="d-flex flex-column align-items-center gap-2 flex-shrink-0 text-muted">
      <Button
        variant="link"
        className="p-0"
        style={{ color: '#234a68' }}
        onClick={moveUp}
        disabled={index === 0}
        aria-label={i18next.t('exerciseCard.moveUp')}
      >
        <i className="fas fa-chevron-up" aria-hidden="true" />
      </Button>
      <span
        ref={dragHandleRef}
        className="d-inline-block"
        style={{ cursor: 'move' }}
        aria-label={i18next.t('exerciseCard.dragHandle')}
      >
        <i className="fas fa-grip-lines" aria-hidden="true" />
      </span>
      <Button
        variant="link"
        className="p-0"
        style={{ color: '#234a68' }}
        onClick={moveDown}
        disabled={index === exerciseCount - 1}
        aria-label={i18next.t('exerciseCard.moveDown')}
      >
        <i className="fas fa-chevron-down" aria-hidden="true" />
      </Button>
    </div>
  );

  const renderMediaSection = (): ReactNode => {
    if (!exerciseValues.exercise) {
      return null;
    }

    return (
      <div className="flex-shrink-0" style={{ width: '180px' }}>
        <div draggable className="w-100" onDragStart={preventDrag}>
          <MediaPlayer exercise={exerciseValues.exercise} />
        </div>
      </div>
    );
  };

  return (
    <Card
      ref={ref}
      className={`border p-2 px-3 mb-2 bg-white ${isDragging ? 'opacity-0' : ''}`}
      data-handler-id={handlerId}
    >
      <Form noValidate>
        <div className="d-flex gap-3 p-3 flex-wrap flex-xl-nowrap">
          {renderReorderControls()}
          {renderMediaSection()}

          <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
            <Form.Group className="mb-0">
              <Form.Label className="d-flex align-items-center mb-1">
                <Switch
                  className="react-switch"
                  title={i18next.t('exerciseCard.form.customDescription')}
                  onColor="#ff2d1f"
                  handleDiameter={15}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  height={18}
                  width={32}
                  onChange={handleCustomDescriptionToggle}
                  checked={exerciseValues.useCustomDescription}
                />
                <span className="ms-2">{i18next.t('exerciseCard.form.customDescription')}</span>
              </Form.Label>
              <Form.Control
                disabled={!exerciseValues.useCustomDescription}
                type="text"
                name={`${fieldBase}.name`}
                id={`name-${index}-${tabIndex}`}
                placeholder={i18next.t('exerciseCard.form.name')}
                value={exerciseValues.name ?? ''}
                onChange={(e) => setExerciseField('name', e.target.value)}
                onBlur={handleBlur}
                isInvalid={exerciseTouched?.name && exerciseErrors?.name}
                title={exerciseValues.name}
              />
              <Form.Control.Feedback type="invalid">{exerciseErrors?.name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className={`mt-2 ${!exerciseValues.useCustomDescription ? 'disabled-box' : ''}`}>
              <LexicalEditor
                key={`${fieldBase}.description`}
                editable={exerciseValues.useCustomDescription}
                value={exerciseValues.description ?? ''}
                placeholder={i18next.t('exerciseCard.form.description')}
                onChange={(value: string) => setExerciseField('description', value)}
                onBlur={() => setFieldTouched(`${fieldBase}.description`, true)}
              />
              {exerciseTouched?.description && exerciseErrors?.description && (
                <div className="w-100 mt-1 small text-danger">{exerciseErrors.description}</div>
              )}
            </Form.Group>
          </div>

          <div className="d-flex flex-column flex-shrink-0 gap-2" style={{ width: isSimpleType ? 'auto' : '280px' }}>
            <div className="d-flex justify-content-end">
              {renderDeleteButton()}
            </div>

            {!isSimpleType && (
              <>
                <div className="d-flex flex-column gap-2">
                  {renderExerciseTypeFields()}
                </div>

                <Form.Group className="mt-1">
                  <Form.Check type="checkbox">
                    <Form.Check.Input
                      className="mt-2"
                      type="checkbox"
                      checked={exerciseValues.skipPause}
                      name={`${fieldBase}.skipPause`}
                      onChange={(e) => setExerciseField('skipPause', e.target.checked)}
                    />
                    <Form.Check.Label>
                      {i18next.t('exerciseCard.form.skipPause')}
                      <OverlayTrigger transition placement="bottom" overlay={skipPausePopover}>
                        <Button variant="link" className="p-0 ps-2">
                          <i className="fas fa-info-circle" />
                        </Button>
                      </OverlayTrigger>
                    </Form.Check.Label>
                  </Form.Check>
                </Form.Group>

                {isVideFeedbackEnabled && (
                  <Form.Group>
                    <Form.Check type="checkbox">
                      <Form.Check.Input
                        className="mt-2"
                        type="checkbox"
                        checked={exerciseValues.needsVideoFeedback}
                        name={`${fieldBase}.needsVideoFeedback`}
                        onChange={(e) => setExerciseField('needsVideoFeedback', e.target.checked)}
                      />
                      <Form.Check.Label>
                        {i18next.t('exerciseCard.form.needsVideoFeedback')}
                      </Form.Check.Label>
                    </Form.Check>
                  </Form.Group>
                )}
              </>
            )}
          </div>
        </div>
      </Form>

      <ConfirmationModal
        show={showConfirmModal}
        title={i18next.t('exerciseCard.deleteExercise.title')}
        body={<p>{i18next.t('exerciseCard.deleteExercise.body')}</p>}
        buttonPositive={i18next.t('generic.delete')}
        buttonNegative={i18next.t('generic.cancel')}
        onPositive={handleRemoveExercise}
        onNegative={() => setShowConfirmModal(false)}
      />
    </Card>
  );
};
